// Motor centralizado de planes, features, límites y trial.
//
// Fuente de verdad:
//   - plan (tabla global)
//   - plan_feature (tabla global)
//   - plan_limite (tabla global)
//   - empresa.plan_id, empresa.trial_*
//   - suscripcion (tabla de historial)
//
// Regla: ninguna feature ni límite se consulta por nombre de plan
// (if (plan === 'pro')). Todo pasa por esta capa.

/**
 * @typedef {Object} EmpresaPlana
 * @property {string} id
 * @property {string} nombre
 * @property {string} estado
 * @property {string|null} plan_id
 * @property {string|null} trial_estado
 * @property {string|null} trial_inicia_en
 * @property {string|null} trial_fin_en
 * @property {Object|null} plan
 * @property {Array<{clave: string, valor: any}>} [plan_features]
 * @property {Array<{recurso: string, limite: number}>} [plan_limites]
 */

/**
 * @typedef {Object} PlanFeature
 * @property {string} plan_id
 * @property {string} clave
 * @property {any} valor
 */

/**
 * @typedef {Object} PlanLimite
 * @property {string} plan_id
 * @property {string} recurso
 * @property {number} limite
 */

// ---------------------------------------------------------------------------
// Helpers básicos
// ---------------------------------------------------------------------------

/**
 * Devuelve el objeto `plan` embebido en la empresa, o null.
 */
export function obtenerPlan(empresa) {
  return empresa?.plan ?? null;
}

/**
 * Devuelve el ID del plan actual, o null si no tiene.
 */
export function obtenerPlanId(empresa) {
  return empresa?.plan_id ?? null;
}

// ---------------------------------------------------------------------------
// Diagnóstico de planes: efectivo vs solicitado
// ---------------------------------------------------------------------------

/**
 * Obtiene el plan efectivo: el de la suscripción activa.
 * Una suscripción pendiente NO es plan efectivo.
 */
export function obtenerPlanEfectivo(empresa) {
  if (!empresa) return null;

  const suscripcionActiva = empresa.suscripcion?.find((s) => s.estado === 'activa');
  if (suscripcionActiva?.plan) {
    return {
      id: suscripcionActiva.plan.id,
      nombre: suscripcionActiva.plan.nombre,
      precio_mensual: Number(suscripcionActiva.plan.precio_mensual || 0),
      limites: suscripcionActiva.plan.plan_limite ?? null,
      features: suscripcionActiva.plan.plan_feature ?? null,
    };
  }

  const planFallback = empresa.plan_prueba ?? empresa.plan;
  if (!planFallback) return null;

  return {
    id: planFallback.id,
    nombre: planFallback.nombre,
    precio_mensual: Number(planFallback.precio_mensual || 0),
    limites: planFallback.plan_limite ?? null,
    features: planFallback.plan_feature ?? null,
  };
}

/**
 * Obtiene el plan solicitado: el de la solicitud pendiente de contratación.
 */
export function obtenerPlanSolicitado(empresa) {
  if (!empresa) return null;

  const solicitudPendiente = empresa.solicitud_contratacion?.find((s) => s.estado === 'pendiente');
  if (!solicitudPendiente?.plan) return null;

  return {
    id: solicitudPendiente.plan.id,
    nombre: solicitudPendiente.plan.nombre,
    precio_mensual: Number(solicitudPendiente.plan.precio_mensual || 0),
    limites: solicitudPendiente.plan?.plan_limite ?? null,
  };
}

/**
 * Diagnóstico completo de planes para una empresa.
 * Separa claramente plan efectivo (suscripción activa) de plan solicitado
 * (solicitud pendiente).
 */
export function diagnosticoPlanes(empresa) {
  const planEfectivo = obtenerPlanEfectivo(empresa);
  const planSolicitado = obtenerPlanSolicitado(empresa);
  const tienePlanPagoPendiente = Boolean(
    planSolicitado && Number(planSolicitado.precio_mensual || 0) > 0,
  );

  let estadoContratacion = null;
  if (tienePlanPagoPendiente) {
    estadoContratacion = 'Pendiente de aprobación';
  } else if (planEfectivo) {
    const precio = Number(planEfectivo.precio_mensual || 0);
    if (precio > 0) {
      estadoContratacion = 'Activo';
    } else {
      estadoContratacion = estaEnTrial(empresa) ? 'Prueba activa' : 'Inactivo';
    }
  } else if (estaEnTrial(empresa)) {
    estadoContratacion = 'Prueba activa';
  }

  return {
    efectivo: planEfectivo,
    solicitado: planSolicitado,
    estado_contratacion: estadoContratacion,
    tiene_plan_pago_pendiente: tienePlanPagoPendiente,
  };
}

/**
 * Verifica si la empresa tiene una contratación de plan de pago pendiente.
 */
export function tieneContratacionPendiente(empresa) {
  return diagnosticoPlanes(empresa).tiene_plan_pago_pendiente;
}

/**
 * Verifica si la empresa está en periodo de trial activo.
 * Comprueba trial_estado='activa' Y que trial_fin_en no haya vencido.
 */
export function estaEnTrial(empresa) {
  if (!empresa) return false;
  if (empresa.trial_estado !== 'activa') return false;
  if (!empresa.trial_fin_en) return false;

  const fin = new Date(empresa.trial_fin_en).getTime();
  return Date.now() < fin;
}

/**
 * Verifica si la empresa tiene trial activo o ya lo convirtió.
 */
export function tieneTrial(empresa) {
  if (!empresa) return false;
  return Boolean(empresa.trial_inicia_en);
}

/**
 * Días restantes de trial.
 * Si expiró o no tiene trial, devuelve 0.
 */
export function diasRestantesTrial(empresa) {
  if (!empresa?.trial_fin_en) return 0;
  const fin = new Date(empresa.trial_fin_en).getTime();
  const ahora = Date.now();
  if (ahora >= fin) return 0;
  return Math.ceil((fin - ahora) / (1000 * 60 * 60 * 24));
}

/**
 * Verifica si el trial está próximo a vencer (dentro de los próximos N días).
 */
export function trialProximoAVencer(empresa, diasUmbral = 2) {
  const restantes = diasRestantesTrial(empresa);
  return restantes > 0 && restantes <= diasUmbral;
}

// ---------------------------------------------------------------------------
// Features
// ---------------------------------------------------------------------------

/**
 * Verifica si la empresa tiene habilitada una feature.
 * Busca en `empresa.plan_features` (array de objetos {clave, valor}).
 * Si no trae el array, consulta el valor por defecto de la feature.
 *
 * @param {EmpresaPlana} empresa
 * @param {string} clave
 * @param {boolean} [defecto=false]
 */
export function tieneFeature(empresa, clave, defecto = false) {
  const planEfectivo = obtenerPlanEfectivo(empresa);
  const features = planEfectivo?.features ?? [];
  let resultado;
  if (!features.length) {
    resultado = defecto;
  } else {
    const feature = features.find((f) => f.clave === clave);
    resultado = feature ? Boolean(feature.valor) : defecto;
  }

  return resultado;
}

// ---------------------------------------------------------------------------
// Límites
// ---------------------------------------------------------------------------

/**
 * Obtiene el límite numérico de un recurso para el plan de la empresa.
 * Busca en `empresa.plan_limites` (array de objetos {recurso, limite}).
 * Si no lo encuentra, devuelve null (sin límite).
 *
 * @param {EmpresaPlana} empresa
 * @param {string} recurso
 * @returns {number|null}
 */
export function obtenerLimite(empresa, recurso) {
  const planEfectivo = obtenerPlanEfectivo(empresa);
  const limites = planEfectivo?.limites ?? [];
  if (!limites.length) return null;

  const limite = limites.find((l) => l.recurso === recurso);
  return limite?.limite ?? null;
}

/**
 * Calcula el consumo actual de un recurso contando filas en la BD.
 * NO usa tabla de consumo: calcula directo desde las tablas reales.
 *
 * Recursos soportados:
 *   - 'productos'
 *   - 'categorias'
 *   - 'administradores'
 *   - 'imagenes_por_producto' (requiere productoId)
 *
 * @param {EmpresaPlana} empresa
 * @param {string} recurso
 * @param {string|null} [extraId] - ID adicional (ej. productoId para imágenes)
 * @returns {number}
 */
export function calcularConsumo(empresa, recurso, _extraId = null) {
  // NOTA: esta función es solo la firma pública. El consumo real se calcula
  // en el panel cliente mediante consultas COUNT a Supabase (ver api/*.js).
  // Esta función existe para centralizar la lógica de interpretación y para
  // que el backend (triggers) no tenga que duplicar la lógica de conteo.
  //
  // El panel cliente debe usar las funciones de COUNT existentes:
  //   - contarProductos() → 'productos'
  //   - contarCategorias() → 'categorias'
  //   - listarUsuarios({ empresaId }) → 'administradores'
  //   - contarImagenesProducto(productoId) → 'imagenes_por_producto'
  //
  // Esta función queda como contrato para el motor de planes.

  const mapa = {
    productos:
      'Se calcula con COUNT en producto WHERE empresa_id = :empresa_id AND eliminado_en IS NULL',
    categorias:
      'Se calcula con COUNT en categoria WHERE empresa_id = :empresa_id AND eliminado_en IS NULL',
    administradores:
      'Se calcula con COUNT en usuario_plataforma WHERE empresa_id = :empresa_id AND rol IN (admin_empresa, editor_empresa) AND estado = activo',
    imagenes_por_producto:
      'Se calcula con COUNT en producto_imagen WHERE producto_id = :producto_id',
  };

  const sql = mapa[recurso];
  if (!sql) return 0;

  // Este es un contrato: el llamador debe ejecutar la consulta SQL apropiada
  // usando su cliente de Supabase. No hacemos consultas aquí para mantener
  // este módulo puro y testeable sin infraestructura.
  return 0;
}

// ---------------------------------------------------------------------------
// Validación de límites
// ---------------------------------------------------------------------------

/**
 * Verifica si la empresa puede CREAR un recurso adicional.
 *
 * @param {EmpresaPlana} empresa
 * @param {string} recurso
 * @param {number|null} [consumoActual] - Si no se pasa, se asume 0 (la UI
 *   debe calcular el consumo real antes de llamar a esta función).
 * @returns {{ permitido: boolean, limite: number|null, consumo: number, mensaje?: string }}
 */
export function puedeCrear(empresa, recurso, consumoActual = null) {
  const limite = obtenerLimite(empresa, recurso);

  // Sin límite definido => siempre permitido
  if (limite === null) {
    return { permitido: true, limite: null, consumo: consumoActual ?? 0 };
  }

  const consumo = consumoActual ?? 0;

  if (consumo >= limite) {
    return {
      permitido: false,
      limite,
      consumo,
      mensaje: `Límite de plan alcanzado: ${consumo}/${limite} ${recurso}. Actualiza tu plan para agregar más.`,
    };
  }

  // Cerca del límite (>= 90%)
  const porcentaje = consumo / limite;
  const aviso = porcentaje >= 0.9 ? ` Estás cerca del límite (${consumo}/${limite}).` : undefined;

  return {
    permitido: true,
    limite,
    consumo,
    aviso,
  };
}

// ---------------------------------------------------------------------------
// Utilidades de contrato
// ---------------------------------------------------------------------------

/**
 * Lista de recursos que tienen límite y deben consultarse vía obtenerLimite.
 */
export const RECURSOS_CON_LIMITE = [
  'productos',
  'categorias',
  'imagenes_por_producto',
  'administradores',
];

/**
 * Mapa de nombres legibles para mostrar en UI.
 */
export const ETIQUETA_RECURSO = {
  productos: 'productos',
  categorias: 'categorías',
  imagenes_por_producto: 'imágenes por producto',
  administradores: 'administradores',
};

/**
 * Formatea el consumo de un recurso para mostrar en UI.
 * Ejemplo: "23 / 50 productos"
 */
export function formatearConsumo(empresa, recurso, consumoActual) {
  const limite = obtenerLimite(empresa, recurso);
  const etiqueta = ETIQUETA_RECURSO[recurso] ?? recurso;

  if (limite === null) {
    return `${consumoActual} ${etiqueta} (sin límite)`;
  }

  return `${consumoActual} / ${limite} ${etiqueta}`;
}
